import { ApolloClient, useApolloClient } from '@apollo/client';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Text,
  Skeleton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Alert,
  AlertDescription,
  AlertIcon,
  useToast,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';

import {
  consts as commonConsts,
  date as commonDate,
  finleyAssert,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';
import { progress } from '@finley-technologies/tape-builder';

import {
  GetRequiredDataTapesQueryGQLType,
  RequiredCashBalanceGQLType,
  RequiredDataTapeGQLType,
  RequiredDataTapeGroupingGQLType,
  RequiredXlsxTapeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';
import {
  CashBalanceTape,
  CashBalanceTapeState,
  CreditFacilityMap,
  DataTape,
  DataTapeMap,
  DataTapeState,
  DataTapeUiStatus,
  UploadCompletionMap,
} from 'types/manualDataTapeUpload';

import { DataTapeErrorView } from './DataTapeErrorView';
import { RequiredDataTapesTable } from './RequiredDataTapesTable';
import { UploadCashBalanceForm } from './UploadCashBalanceForm';
import { UploadDataTapeForm } from './UploadDataTapeForm';
import { colors } from 'packages/common-frontend/colors';
import { Select } from 'packages/common-frontend/components/Select';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useGetCreditFacilitiesQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { GET_PIPELINE_UPLOAD_SIGNED_URL } from 'packages/files/requests/files.gql';
import { presignedPostUpload } from 'packages/files/ui/utils';
import { useGetRequiredDataTapesQuery } from 'packages/manualDataTapeUploads/requests/requests.gql.generated';

import 'handsontable/dist/handsontable.full.min.css';

export const getTitleCaseExternalFilename = (filename: string): string => {
  const splitOnForwardSlash = filename.split('/');
  return commonUtils.toTitleCase(splitOnForwardSlash.slice(1).join(' '));
};

export const getProgressSummariesFromRequiredDataTapes = (
  data: GetRequiredDataTapesQueryGQLType | undefined,
  onlyCsv?: boolean,
): progress.ProgressSummary[] => {
  return _.chain(data?.getRequiredDataTapes.requiredDataTapesGroupings)
    .flatMap(({ requiredDataTapes, requiredXlsxTapes }) => {
      if (onlyCsv) {
        return requiredDataTapes;
      }
      return [...requiredDataTapes, ...requiredXlsxTapes];
    })
    .map((tape) => tape.progressSummary as Util.Maybe<progress.ProgressSummary>)
    .filter(commonTypeguards.isNotNullOrUndefined)
    .value();
};

// The helper throws and captures a sentry exception if an error occurs
export const directUpload = async ({
  client,
  file,
  organizationId,
  artifactId,
  reportingDate,
  onEventCallback,
}: {
  client: ApolloClient<object>;
  file: File;
  organizationId: string;
  artifactId: string;
  reportingDate: Util.FullDateStr;
  onEventCallback: (loaded: number, total: number) => void;
}): Promise<string> => {
  // The helper throws and captures a sentry exception if an error occurs
  const { queryResponse } = await presignedPostUpload<
    {
      organizationId: string;
      artifactId: string;
      reportingDate: Util.FullDateStr;
    },
    { artifactInstanceId: string }
  >({
    client,
    file,
    query: GET_PIPELINE_UPLOAD_SIGNED_URL,
    queryVariables: {
      organizationId,
      artifactId,
      reportingDate,
    },
    progressEventCallback: ({ loaded, total }) => {
      onEventCallback(loaded, total);
    },
  });

  finleyAssert(
    queryResponse.artifactInstanceId,
    'BBPL artifact instance id missing.',
  );

  return queryResponse.artifactInstanceId;
};

// The current assumption is all-or-nothing for cash balance feeds. For a given facility, either
// all bank accounts are configured to pull balances from API, or none of them are (and hence we
// the facility "needs upload").
const needsUpload = (
  cashBalanceTape: CashBalanceTape | RequiredCashBalanceGQLType,
): boolean => cashBalanceTape.accountTypesWithConnectionProvider.length === 0;

export const getFileVersionForReportingDate = (
  reportingDate: Util.FullDateStr,
  requiredDataTapeGroupings: RequiredDataTapeGroupingGQLType[],
  newestTapePredicate: (
    tape:
      | RequiredDataTapeGQLType
      | RequiredXlsxTapeGQLType
      | RequiredCashBalanceGQLType,
  ) => boolean,
  onlyCsv?: boolean,
): number | null => {
  const tapes = requiredDataTapeGroupings.flatMap((group) => {
    if (onlyCsv) {
      return group.requiredDataTapes;
    }
    return [
      ...group.requiredDataTapes,
      ...group.requiredXlsxTapes,
      ...group.requiredCashBalances.filter(needsUpload),
    ];
  });
  // For the newly uploaded tape, the backend will have selected the correct file version, which all
  // other tape uploads will then need to match.
  const newestTape = tapes.find(
    (tape) =>
      newestTapePredicate(tape) && tape.currentReportingDate === reportingDate,
  );
  return newestTape?.currentFileVersion ?? null;
};

type DatePickerProps = {
  isDatePickerLocked: boolean;
  selectedReportingDate: Util.FullDateStr;
  setSelectedReportingDate: React.Dispatch<
    React.SetStateAction<Util.FullDateStr>
  >;
  selectedFileVersion: number | undefined;
};
const DatePicker = ({
  isDatePickerLocked,
  selectedReportingDate,
  setSelectedReportingDate,
  selectedFileVersion,
}: DatePickerProps) => {
  // Explicitly setting this to avoid issues with reporting date and date picker conversions
  // Internal usage expects default TZ=America/New_York
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const clientCurrentDate = commonDate.dateToFullDateString(
    new Date(),
    clientTimeZone,
  );

  // TODO(sarah): updated data picker and warning messages from mocks.

  if (isDatePickerLocked) {
    return (
      <>
        <Flex alignItems="center">
          <Text>Reporting date locked: {selectedReportingDate}</Text>
          {selectedFileVersion !== undefined && (
            <Tooltip
              label={`File version locked: ${selectedFileVersion}`}
              placement="right"
            >
              <InfoOutlineIcon marginLeft="10px" />
            </Tooltip>
          )}
        </Flex>
        <Text mt="10px">
          To re-upload files, or restart, please refresh the page.
        </Text>
      </>
    );
  }

  return (
    <Flex mt="10px" alignItems="baseline">
      <Text mr="10px">Please select your reporting date:</Text>
      <SimpleDatePicker
        tz={clientTimeZone}
        minDate={null}
        maxDate={commonDate.fullDateStrToDate(
          commonDate.addBusinessDays(
            clientCurrentDate,
            commonConsts.maxFutureBusinessDaysReportingDate,
          ),
        )}
        selectedDate={commonDate.fullDateStrToDate(selectedReportingDate)}
        showTimeSelect={false}
        setSelectedDate={(date: Date) => {
          const fullDateStr = commonDate.dateToFullDateString(
            date,
            clientTimeZone,
          );
          setSelectedReportingDate(fullDateStr);
        }}
        width="150px"
      />
    </Flex>
  );
};

type DataTapeContainerProps = { organizationId: string };
export const DataTapeContainer = ({
  organizationId,
}: DataTapeContainerProps) => {
  // Explicitly setting this to avoid issues with reporting date and date picker conversions
  // Internal usage expects default TZ=America/New_York
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const clientCurrentDate = commonDate.dateToFullDateString(
    new Date(),
    clientTimeZone,
  );

  // Set up state variables
  const [
    latestSeenReportingDate,
    setLatestSeenReportingDate,
  ] = useState<Util.FullDateStr>();
  const [
    selectedReportingDate,
    setSelectedReportingDate,
  ] = useState<Util.FullDateStr>(clientCurrentDate);
  const [isDatePickerLocked, setIsDatePickerLocked] = useState(false);
  const [selectedFileVersion, setSelectedFileVersion] = useState<number>();

  const [modalDataTape, setModalDataTape] = useState<DataTape>();
  const [modalCashBalance, setModalCashBalance] = useState<CashBalanceTape>();

  const [creditFacilityId, setCreditFacilityId] = useState<string | null>(null);

  const onClose = () => {
    setModalDataTape(undefined);
    setModalCashBalance(undefined);
  };
  const [
    uploadCompletionMap,
    setUploadCompletionMap,
  ] = useState<UploadCompletionMap>({});

  /**
   * Indicate if any single file has been uploaded successfully during the page session
   * Used to adjust success message display to avoid showing on initial page render
   * when all files have been uploaded for a prior run
   */
  const sessionUploadSuccess = selectedFileVersion !== undefined;

  const client = useApolloClient();
  const toast = useToast();

  const {
    refetch: refetchLatestSeenReportingDate,
  } = useGetRequiredDataTapesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      organizationId,
      // Here, we're interested in latest seen reporting date regardless of what date we're
      // currently uploading for. This is opposed to the next call to `useGetRequiredDataTapesQuery`
      // which is specifically interested in the uploads <= the selected reporting date.
      reportingDate: null,
      omitOrgProvidedInputGraphs: true,
    },
    onCompleted: (data) => {
      const response = data.getRequiredDataTapes.requiredDataTapesGroupings;
      const latestReportingDate = _.max(
        _.map(response, (group) => group.latestReportingDate),
      );
      setLatestSeenReportingDate(latestReportingDate ?? undefined);
    },
    fetchPolicy: 'network-only',
  });

  // Retrieve required data tapes
  const {
    data: requiredDataTapesData,
    refetch: refetchRequiredDataTapes,
    startPolling: startPollingRequiredDataTapes,
    stopPolling: stopPollingRequiredDataTapes,
  } = useGetRequiredDataTapesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      organizationId,
      reportingDate: selectedReportingDate,
      omitOrgProvidedInputGraphs: true,
    },
    fetchPolicy: 'network-only',
  });
  useEffect(() => {
    const progressSummaries = getProgressSummariesFromRequiredDataTapes(
      requiredDataTapesData,
    );
    if (progressSummaries.some(({ isRunning }) => isRunning)) {
      startPollingRequiredDataTapes(3000);
    } else {
      stopPollingRequiredDataTapes();
    }
  }, [
    requiredDataTapesData,
    startPollingRequiredDataTapes,
    stopPollingRequiredDataTapes,
  ]);

  // Slugs for data tapes for which uploads are in progress.
  const [inProgressSlugs, setInProgressSlugs] = useState<string[]>([]);
  // Facility ids for which cash balance upload requests are pending.
  const [inProgressFacilityIds, setInProgressFacilityIds] = useState<string[]>(
    [],
  );

  // Retrieve credit facility information for current organization
  const { data: creditFacilitiesData } = useGetCreditFacilitiesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { organizationId },
    onCompleted: (response) => {
      const creditFacilities = response.creditFacilities;
      if (creditFacilities.length > 0) {
        setCreditFacilityId((prevId) => prevId ?? creditFacilities[0].id);
      } else {
        setCreditFacilityId(null);
      }
    },
  });

  // Avoid render until necessary data is available
  if (
    !requiredDataTapesData ||
    !creditFacilitiesData ||
    !latestSeenReportingDate ||
    !selectedReportingDate
  ) {
    return (
      <Skeleton data-testid="loading-manual-uploader-view" height="600px" />
    );
  }

  const creditFacilities = _.map(
    creditFacilitiesData.creditFacilities,
    (creditFacility) => ({
      ...creditFacility,
      formattedSlug: commonUtils.creditFacilitySlugToName(creditFacility.slug),
    }),
  );
  const creditFacilitiesMap = _.chain(creditFacilities)
    .keyBy(({ id }) => id)
    .value() as CreditFacilityMap;

  const dataTapes: DataTapeMap = _.chain(
    requiredDataTapesData.getRequiredDataTapes.requiredDataTapesGroupings,
  )
    .flatMap((group) =>
      [group.requiredDataTapes, group.requiredXlsxTapes].flat(),
    )
    .map((tape): DataTapeState | null => {
      const progressSummary: Util.Maybe<progress.ProgressSummary> =
        tape.progressSummary;
      let localStatus: DataTapeUiStatus;
      if (inProgressSlugs.includes(tape.artifactSlug)) {
        localStatus = 'uploading';
      } else if (
        progressSummary?.status === 'inProgress' &&
        sessionUploadSuccess &&
        selectedReportingDate === tape.currentReportingDate &&
        selectedFileVersion === tape.currentFileVersion
      ) {
        localStatus = 'processing';
      } else if (
        progressSummary?.status === 'error' &&
        selectedReportingDate === tape.currentReportingDate
      ) {
        localStatus = 'error';
      } else if (
        sessionUploadSuccess &&
        selectedReportingDate === tape.currentReportingDate &&
        selectedFileVersion === tape.currentFileVersion
      ) {
        localStatus = 'success';
      } else {
        localStatus = 'none';
      }
      switch (tape.__typename) {
        case 'RequiredDataTape':
          return {
            ...tape,
            fileType: 'csv',
            key: tape.artifactSlug,
            localStatus,
            progressSummary: tape.progressSummary ?? null,
          };
        case 'RequiredXlsxTape':
          return {
            ...tape,
            fileType: 'xlsx',
            key: tape.artifactSlug,
            localStatus,
            progressSummary: tape.progressSummary ?? null,
          };
        default:
          return null;
      }
    })
    .compact()
    .keyBy(({ key }) => key)
    .value();

  const cashBalances: CashBalanceTapeState[] = _.chain(
    requiredDataTapesData.getRequiredDataTapes.requiredDataTapesGroupings,
  )
    .flatMap((group) => group.requiredCashBalances)
    .map(
      (cash): CashBalanceTapeState => {
        let localStatus: DataTapeUiStatus;
        if (inProgressFacilityIds.includes(cash.creditFacilityId)) {
          localStatus = 'uploading';
        } else if (
          sessionUploadSuccess &&
          selectedReportingDate === cash.currentReportingDate &&
          selectedFileVersion === cash.currentFileVersion
        ) {
          localStatus = 'success';
        } else {
          localStatus = 'none';
        }
        return {
          ...cash,
          key: cash.artifactSlug,
          localStatus,
          progressSummary: null,
        };
      },
    )
    .value();

  const uploadFile = async (
    file: File,
    dataTape: DataTape | undefined,
  ): Promise<void> => {
    if (!dataTape || inProgressSlugs.includes(dataTape.artifactSlug)) return;

    setInProgressSlugs((prevState) => [...prevState, dataTape.artifactSlug]);
    setIsDatePickerLocked(true);

    try {
      // @makarand-temp: directUpload
      await directUpload({
        client,
        file,
        organizationId,
        artifactId: dataTape.artifactId,
        reportingDate: selectedReportingDate,
        onEventCallback: (loaded, total) => {
          const percentCompleted = Math.ceil((loaded * 100) / total);
          setUploadCompletionMap((prevState) => ({
            ...prevState,
            [dataTape.artifactSlug]: percentCompleted,
          }));
        },
      });

      const [result] = await Promise.all([
        refetchRequiredDataTapes(), // Will have changed due to our upload.
        refetchLatestSeenReportingDate(), // May have changed due to our upload.
      ]);

      const fileVersion = getFileVersionForReportingDate(
        selectedReportingDate,
        result.data.getRequiredDataTapes.requiredDataTapesGroupings,
        (tape) => tape.artifactSlug === dataTape.artifactSlug,
      );
      if (fileVersion !== null) setSelectedFileVersion(fileVersion);
    } catch (err: any) {
      toast({
        ...errorToastConfig,
        id: undefined, // Avoid merging timeouts if multiple errors.
        title:
          'Error processing upload. Please try again, and contact your Finley representative if this persists.',
      });
    } finally {
      setInProgressSlugs((prevState) =>
        prevState.filter((slug) => slug !== dataTape.artifactSlug),
      );
    }
  };

  const onCashBalanceUploadStart = (cfId: string) => {
    setInProgressFacilityIds((prevState) => [...prevState, cfId]);
    setIsDatePickerLocked(true);
  };

  const onCashBalanceUploadSuccess = async (cfId: string) => {
    try {
      const result = await refetchRequiredDataTapes();
      const fileVersion = getFileVersionForReportingDate(
        selectedReportingDate,
        result.data.getRequiredDataTapes.requiredDataTapesGroupings,
        (tape) =>
          tape.__typename === 'RequiredCashBalance' &&
          tape.creditFacilityId === cfId,
      );
      if (fileVersion !== null) setSelectedFileVersion(fileVersion);
    } finally {
      setInProgressFacilityIds((prevState) =>
        prevState.filter((id) => id !== cfId),
      );
    }
  };

  const dataTapesUploaded = _.every(
    dataTapes,
    ({ localStatus }) => localStatus === 'success',
  );
  const cashBalancesUploaded = _.every(
    cashBalances.filter(needsUpload),
    (cashBalance) => cashBalance.localStatus === 'success',
  );
  const displaySuccess = dataTapesUploaded && cashBalancesUploaded;

  return (
    <>
      <Box>
        <Box w="500px" m="32px 0">
          {/* !TODO @eldarshamukhamedov This should use the standardized CreditFacilitySeletor */}
          <Select
            height="4.5rem"
            value={creditFacilityId}
            options={creditFacilities.map(
              ({ id, formattedSlug, borrowerEntityName }) => ({
                value: id,
                label: (
                  <VStack as="span" alignItems="flex-start" spacing={1}>
                    <Text
                      as="span"
                      fontSize="sm"
                      fontWeight="bold"
                      lineHeight="1.2rem"
                    >
                      {formattedSlug}
                    </Text>
                    <Text
                      as="span"
                      color={colors.black[70]}
                      lineHeight="1.5rem"
                    >
                      {borrowerEntityName}
                    </Text>
                  </VStack>
                ),
              }),
            )}
            onChange={(newCreditFacilityId) => {
              if (newCreditFacilityId) {
                setCreditFacilityId(newCreditFacilityId);
              }
            }}
            inputFloatingLabel="Credit Facility"
            inputFloatingLabelBackgroundColor="white"
          />
        </Box>
        <DatePicker
          isDatePickerLocked={isDatePickerLocked}
          selectedReportingDate={selectedReportingDate}
          setSelectedReportingDate={setSelectedReportingDate}
          selectedFileVersion={selectedFileVersion}
        />
        {selectedReportingDate < latestSeenReportingDate && (
          <Alert status="warning" mt="16px">
            <AlertIcon />
            <AlertDescription>
              Older data tapes may be uploaded for data backfill, but cannot be
              used to submit funding requests. We use the current Credit
              Agreement for Borrowing Base calculations. Older data tapes may
              cause errors or discrepancies.
            </AlertDescription>
          </Alert>
        )}
      </Box>

      <Box>
        <RequiredDataTapesTable
          dataTapes={dataTapes}
          cashBalances={cashBalances}
          currentCreditFacilityId={creditFacilityId}
          setModalDataTape={setModalDataTape}
          setModalCashBalance={setModalCashBalance}
          creditFacilitiesMap={creditFacilitiesMap}
          selectedReportingDate={selectedReportingDate}
          selectedFileVersion={selectedFileVersion || 1}
          uploadCompletionMap={uploadCompletionMap}
        />
        <Modal isOpen={Boolean(modalDataTape)} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent padding="0px" pt="8px" width="auto" minW="850px">
            <ModalHeader fontSize="2xl">
              {modalDataTape
                ? `Upload ${getTitleCaseExternalFilename(
                    modalDataTape.externalFilename,
                  )}`
                : 'Upload File'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <UploadDataTapeForm
                dataTape={modalDataTape}
                allowedExtensions={[modalDataTape?.fileType ?? 'csv']}
                onClose={onClose}
                handleFile={(file) => uploadFile(file, modalDataTape)}
              />
            </ModalBody>
          </ModalContent>
        </Modal>
        <Modal isOpen={Boolean(modalCashBalance)} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent padding="0px" pt="8px" width="auto" minW="850px">
            <ModalHeader fontSize="2xl">Upload Cash Balance</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {modalCashBalance && (
                <>
                  <Text fontWeight="bold" mb="1rem" fontSize="xl">
                    {
                      creditFacilitiesMap[modalCashBalance.creditFacilityId]
                        .formattedSlug
                    }
                  </Text>
                  <UploadCashBalanceForm
                    organizationId={organizationId}
                    creditFacilityId={modalCashBalance.creditFacilityId}
                    reportingDate={selectedReportingDate}
                    accept={{
                      'image/png': ['.png'],
                    }}
                    onClose={onClose}
                    onLoading={onCashBalanceUploadStart}
                    onSubmit={async () => {}}
                    onSuccess={onCashBalanceUploadSuccess}
                  />
                </>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
        {displaySuccess && (
          <Alert status="success" mt="20px">
            <AlertIcon />
            <AlertDescription textColor="green">
              Your uploads were successful! Please check your email for the
              appropriate next steps.
            </AlertDescription>
          </Alert>
        )}
      </Box>

      <Box mt="40px">
        {_.chain(dataTapes)
          .values()
          .sortBy(({ key }) => key)
          .filter(
            ({ currentReportingDate }) =>
              currentReportingDate === selectedReportingDate,
          )
          .flatMap(
            ({ progressSummary }) => progressSummary?.progressBreakdown ?? [],
          )
          .map((step) =>
            step.status.status === 'error' && step.status.errors ? (
              <DataTapeErrorView errors={step.status.errors} numOfRows={25} />
            ) : null,
          )
          .value()}
      </Box>
    </>
  );
};
